import { useState, useEffect, type FC, type ReactNode } from "react";
import {
  AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, RadialBarChart, RadialBar,
} from "recharts";
import styles from "./Home.module.css";

/* ─── DESIGN TOKENS ─── */
const T = {
  blue:       "#3f59de",
  blueLight:  "#5a75f0",
  orange:     "#F97316",
  orangeDeep: "#EA580C",
  text:       "#0c1029",
  dim:        "#525c7a",
  muted:      "#8a93b0",
} as const;

/* ─── TYPES ─── */
interface StatItem {
  label: string;
  value: string;
  change: string;
  pos: boolean;
  icon: ReactNode;
  accent: "blue" | "orange";
}

interface RevenuePoint { m: string; v: number; s: number }
interface WeeklyPoint  { d: string; a: number }
interface PlanItem     { name: string; value: number; color: string }

interface ApiItem {
  name: string; type: string; status: string;
  uptime: number; tenants: number; sync: string;
}

interface DomainItem {
  domain: string; status: string; ssl: string;
  dns: string; time: string;
}

interface FunnelItem   { stage: string; count: number; pct: number }
interface ActivityItem  { text: string; detail: string; time: string; dotColor: string }
interface HealthMetric  { name: string; value: number; color: string }
interface SparkItem     { label: string; val: string; spark: number[]; color: string }
interface StatusStyleResult { bg: string; color: string }

/* ─── SVG ICONS ─── */
const IconUsers: FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconCheckCircle: FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const IconTrendingUp: FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const IconDollarSign: FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const IconCreditCard: FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const IconGlobe: FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

/* ─── DATA ─── */
const revenueData: RevenuePoint[] = [
  { m: "Jul", v: 32000, s: 62 }, { m: "Aug", v: 35000, s: 67 }, { m: "Sep", v: 38000, s: 71 },
  { m: "Oct", v: 41000, s: 74 }, { m: "Nov", v: 44000, s: 78 }, { m: "Dec", v: 47000, s: 80 },
  { m: "Jan", v: 50000, s: 82 }, { m: "Feb", v: 53000, s: 84 }, { m: "Mar", v: 56300, s: 87 },
];

const weeklyData: WeeklyPoint[] = [
  { d: "Mon", a: 4200 }, { d: "Tue", a: 6800 }, { d: "Wed", a: 5400 },
  { d: "Thu", a: 7200 }, { d: "Fri", a: 8100 }, { d: "Sat", a: 5800 }, { d: "Sun", a: 4900 },
];

const plansData: PlanItem[] = [
  { name: "Starter", value: 124, color: T.blue },
  { name: "Pro",     value: 89,  color: T.orange },
  { name: "Ent.",    value: 35,  color: T.orangeDeep },
];

const apiData: ApiItem[] = [
  { name: "Amadeus",    type: "Flight", status: "Connected", uptime: 99.8, tenants: 142, sync: "2m" },
  { name: "Sabre",      type: "Flight", status: "Connected", uptime: 99.2, tenants: 67,  sync: "5m" },
  { name: "Travelport", type: "Flight", status: "Degraded",  uptime: 94.1, tenants: 38,  sync: "18m" },
  { name: "Hotelbeds",  type: "Hotel",  status: "Connected", uptime: 99.5, tenants: 98,  sync: "1m" },
  { name: "Ratehawk",   type: "Hotel",  status: "Connected", uptime: 98.9, tenants: 52,  sync: "8m" },
];

const domainData: DomainItem[] = [
  { domain: "travelwala.com", status: "Live",         ssl: "Active",  dns: "Resolved", time: "10:32" },
  { domain: "bajijlinks.pk",  status: "Provisioning", ssl: "Pending", dns: "Resolved", time: "10:28" },
  { domain: "skyroutes.io",   status: "Live",         ssl: "Active",  dns: "Resolved", time: "09:55" },
  { domain: "jettrail.net",   status: "Failed",       ssl: "Error",   dns: "Pending",  time: "09:41" },
];

const funnel: FunnelItem[] = [
  { stage: "Sign Up",   count: 312, pct: 100 },
  { stage: "Payment",   count: 274, pct: 87.8 },
  { stage: "Domain",    count: 252, pct: 80.8 },
  { stage: "Provision", count: 248, pct: 79.5 },
  { stage: "API Setup", count: 231, pct: 74.0 },
  { stage: "Live",      count: 218, pct: 69.9 },
];

const activity: ActivityItem[] = [
  { text: "Al-Safar Travel registered",      detail: "Enterprise · New",  time: "3m",  dotColor: T.blue },
  { text: "$299 received — JetStream",       detail: "Pro Plan",          time: "8m",  dotColor: T.orange },
  { text: "SSL renewed · travelwala.com",    detail: "Auto-renewed",      time: "15m", dotColor: T.blue },
  { text: "New API key — Hotelbeds",         detail: "skyways.travel",    time: "22m", dotColor: T.orange },
  { text: "Provisioning failed · nexvoyage", detail: "Retry scheduled",   time: "34m", dotColor: T.orangeDeep },
  { text: "$499 payment · JetTrail",         detail: "Enterprise",        time: "1h",  dotColor: T.orange },
];

const stats: StatItem[] = [
  { label: "Total Agencies",     value: "248",      change: "+12",  pos: true,  icon: <IconUsers color={T.blue} />,        accent: "blue" },
  { label: "Active Subscriptions", value: "218",    change: "+8",   pos: true,  icon: <IconCheckCircle color={T.orange} />, accent: "orange" },
  { label: "MRR",                value: "$52,300",  change: "+18%", pos: true,  icon: <IconTrendingUp color={T.blue} />,    accent: "blue" },
  { label: "Total Revenue",      value: "$482,600", change: "+24%", pos: true,  icon: <IconDollarSign color={T.orange} />,  accent: "orange" },
  { label: "Payments Today",     value: "34",       change: "+5",   pos: true,  icon: <IconCreditCard color={T.blue} />,    accent: "blue" },
  { label: "Active Domains",     value: "231",      change: "+8",   pos: true,  icon: <IconGlobe color={T.orange} />,       accent: "orange" },
];

/* ─── HELPERS ─── */
const statusStyle = (s: string): StatusStyleResult => {
  const map: Record<string, StatusStyleResult> = {
    Connected:    { bg: "rgba(63,89,222,0.06)",   color: T.blue,        },
    Live:         { bg: "rgba(63,89,222,0.06)",   color: T.blue,        },
    Active:       { bg: "rgba(63,89,222,0.06)",   color: T.blue,        },
    Resolved:     { bg: "rgba(63,89,222,0.06)",   color: T.blue,        },
    Degraded:     { bg: "rgba(249,115,22,0.06)",  color: T.orangeDeep,  },
    Pending:      { bg: "rgba(249,115,22,0.06)",  color: T.orangeDeep,  },
    Provisioning: { bg: "rgba(249,115,22,0.06)",  color: T.orange,      },
    Failed:       { bg: "rgba(234,88,12,0.06)",   color: "#c2410c",     },
    Error:        { bg: "rgba(234,88,12,0.06)",   color: "#c2410c",     },
  };
  return map[s] || { bg: "#f1f5f9", color: "#64748b" };
};

/* ─── ANIMATED NUMBER — top-level, stable mount ─── */
const AnimNum: FC<{ target: string }> = ({ target }) => {
  const num = parseInt(String(target).replace(/[^0-9]/g, "")) || 0;
  const [val, setVal] = useState(num);

  useEffect(() => {
    if (num === 0) { setVal(0); return; }
    let current = 0;
    const duration = 900;
    const stepMs = 16;
    const increment = num / (duration / stepMs);

    const id = setInterval(() => {
      current += increment;
      if (current >= num) {
        setVal(num);
        clearInterval(id);
      } else {
        setVal(Math.floor(current));
      }
    }, stepMs);

    return () => clearInterval(id);
  }, [num]);

  const raw = String(target);
  if (raw.includes("$")) return <>${val.toLocaleString()}</>;
  return <>{val.toLocaleString()}</>;
};

/* ─── MICRO COMPONENTS ─── */
const StatusPill: FC<{ s: string }> = ({ s }) => {
  const st = statusStyle(s);
  return (
    <span className={styles.statusPill} style={{ background: st.bg, color: st.color }}>
      <span className={styles.statusDot} style={{ background: st.color }} />
      {s}
    </span>
  );
};

const Card: FC<{ children: ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`${styles.card} ${className}`}>{children}</div>
);

const CardHead: FC<{ title: string; sub?: string; right?: ReactNode }> = ({ title, sub, right }) => (
  <div className={styles.cardHead}>
    <div>
      <div className={styles.cardTitle}>{title}</div>
      {sub && <div className={styles.cardSub}>{sub}</div>}
    </div>
    {right}
  </div>
);

const TooltipCustom: FC<{
  active?: boolean;
  payload?: Array<{ color: string; value: number | string; name: string }>;
  label?: string;
  prefix?: string;
}> = ({ active, payload, label, prefix = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltipBox}>
      <div className={styles.tooltipLabel}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} className={styles.tooltipRow} style={{ marginBottom: i < payload.length - 1 ? 2 : 0 }}>
          <span className={styles.tooltipDot} style={{ background: p.color }} />
          <span className={styles.tooltipValue}>
            {prefix}{typeof p.value === "number" && p.value > 999 ? `$${(p.value / 1000).toFixed(1)}k` : p.value}
          </span>
          <span className={styles.tooltipName}>{p.name}</span>
        </div>
      ))}
    </div>
  );
};

/* ─── STAT CARDS — top-level so it never remounts ─── */
const StatCards: FC = () => (
  <div className={styles.statsGrid}>
    {stats.map((s, i) => {
      const isOrange = s.accent === "orange";
      return (
        <div key={i} className={styles.statCard}>
          <div className={styles.statCardInner}>
            <div className={styles.statCardLeft}>
              <div className={styles.statIconBox}
                style={{ background: isOrange ? "rgba(249,115,22,0.08)" : "rgba(63,89,222,0.08)" }}>
                {s.icon}
              </div>
            </div>
            <div className={styles.statCardRight}>
              <div className={styles.statLabelRow}>
                <span className={styles.statLabel}>{s.label}</span>
                <span className={s.pos ? styles.statChangePositive : styles.statChangeNegative}>
                  {s.pos ? "↑" : "↓"} {s.change}
                </span>
              </div>
              <div className={styles.statValue}><AnimNum target={s.value} /></div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

/* ═══════════════════════════════════
   HOME SCREEN
═══════════════════════════════════ */
const HomeScreen: FC = () => {

  /* ── REVENUE CHART ── */
  const RevenueChart: FC = () => (
    <Card className={`${styles.spanCol4} ${styles.rowCard1}`}>
      <div className={styles.accentBarOrange} />
      <CardHead
        title="Revenue Overview"
        sub="Monthly trend · Last 9 months"
        right={
          <div className={styles.revenueRight}>
            <div className={styles.revenueValue}>
              <div className={styles.revenueAmount}>$56,300</div>
              <div className={styles.revenueTrend}>↑ 18% vs last month</div>
            </div>
          </div>
        }
      />
      <div className={styles.chartBody}>
        <ResponsiveContainer width="100%" height={148}>
          <AreaChart data={revenueData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={T.orange} stopOpacity={0.18} />
                <stop offset="100%" stopColor={T.orange} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gSub" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={T.blue} stopOpacity={0.12} />
                <stop offset="100%" stopColor={T.blue} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="strokeOrange" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={T.orangeDeep} />
                <stop offset="100%" stopColor={T.orange} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(63,89,222,0.05)" vertical={false} />
            <XAxis dataKey="m" tick={{ fontSize: 9, fill: T.muted, fontFamily: "DM Sans" }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 9, fill: T.muted }} tickLine={false} axisLine={false} tickFormatter={(v: number) => `$${v / 1000}k`} />
            <Tooltip content={<TooltipCustom />} />
            <Area type="monotone" dataKey="v" name="Revenue" stroke="url(#strokeOrange)" strokeWidth={2.5} fill="url(#gRev)" dot={false} />
            <Area type="monotone" dataKey="s" name="Subs" stroke={T.blue} strokeWidth={1.5} fill="url(#gSub)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
        <div className={styles.legendRow}>
          <div className={styles.legendItem}>
            <span className={styles.legendLine} style={{ background: `linear-gradient(90deg, ${T.orangeDeep}, ${T.orange})` }} />
            Revenue (USD)
          </div>
          <div className={styles.legendItem}>
            <span className={styles.legendLine} style={{ background: T.blue }} />
            Subscriptions
          </div>
        </div>
      </div>
    </Card>
  );

  /* ── WEEKLY PAYMENTS ── */
  const WeeklyChart: FC = () => (
    <Card className={`${styles.spanCol3} ${styles.rowCard2}`}>
      <div className={styles.accentBarBlue} />
      <CardHead
        title="Weekly Payments"
        sub="This week · USD"
        right={
          <div>
            <span className={styles.weeklyHeadValue}>$42,400 </span>
            <span className={styles.weeklyHeadTrend}>↑5%</span>
          </div>
        }
      />
      <div className={styles.chartBody}>
        <ResponsiveContainer width="100%" height={148}>
          <BarChart data={weeklyData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barSize={20}>
            <defs>
              <linearGradient id="gBar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={T.blueLight} />
                <stop offset="100%" stopColor={T.blue} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(63,89,222,0.05)" vertical={false} />
            <XAxis dataKey="d" tick={{ fontSize: 9, fill: T.muted }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 9, fill: T.muted }} tickLine={false} axisLine={false} tickFormatter={(v: number) => `$${v / 1000}k`} />
            <Tooltip content={<TooltipCustom prefix="$" />} />
            <Bar dataKey="a" name="Amount" fill="url(#gBar)" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );

  /* ── PLANS PIE ── */
  const PlansChart: FC = () => {
    const total = plansData.reduce((a, b) => a + b.value, 0);
    return (
      <Card className={`${styles.spanCol2} ${styles.rowCard3}`}>
        <CardHead title="Plan Split" sub="Active subscriptions" />
        <div className={styles.pieBody}>
          <div className={styles.pieWrapper}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={plansData} cx="50%" cy="50%" innerRadius={36} outerRadius={52}
                  paddingAngle={3} dataKey="value" strokeWidth={0}>
                  {plansData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip content={({ active, payload }: { active?: boolean; payload?: any }) =>
                  active && payload?.length ? (
                    <div className={styles.pieTooltip}><b>{payload[0].name}</b>: {payload[0].value}</div>
                  ) : null
                } />
              </PieChart>
            </ResponsiveContainer>
            <div className={styles.pieCenter}>
              <div className={styles.pieCenterValue}>{total}</div>
              <div className={styles.pieCenterLabel}>total</div>
            </div>
          </div>
          <div className={styles.planList}>
            {plansData.map((p, i) => (
              <div key={i} className={styles.planRow}>
                <span className={styles.planDot} style={{ background: p.color }} />
                <span className={styles.planName}>{p.name}</span>
                <span className={styles.planValue}>{p.value}</span>
                <span className={styles.planPct}>{Math.round((p.value / total) * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  };

  /* ── API TABLE ── */
  const ApiTable: FC = () => (
    <Card className={`${styles.spanCol5} ${styles.rowCard4}`}>
      <div className={styles.accentBarBlue} />
      <CardHead title="API Connectivity" sub="Real-time supplier status"
        right={<button className={styles.viewBtn}>View all →</button>} />
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr className={styles.tableHeadRow}>
              {["API", "Type", "Status", "Uptime", "Tenants", "Last Sync"].map((h, i) => (
                <th key={i} className={styles.tableHeadCell}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {apiData.map((a, i) => (
              <tr key={i} className={styles.tableRow}>
                <td className={styles.tableCell}><div className={styles.apiName}>{a.name}</div></td>
                <td className={styles.tableCell}><span className={styles.apiType}>{a.type}</span></td>
                <td className={styles.tableCell}><StatusPill s={a.status} /></td>
                <td className={styles.tableCell}>
                  <div className={styles.uptimeBar}>
                    <div className={styles.uptimeTrack}>
                      <div className={styles.uptimeFill}
                        style={{
                          width: `${a.uptime}%`,
                          background: a.uptime > 98
                            ? T.blue
                            : `linear-gradient(90deg, ${T.orangeDeep}, ${T.orange})`,
                        }} />
                    </div>
                    <span className={styles.uptimeText}>{a.uptime}%</span>
                  </div>
                </td>
                <td className={styles.tableCell}><span className={styles.tenantText}>{a.tenants}</span></td>
                <td className={styles.tableCell}><span className={styles.syncText}>{a.sync} ago</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );

  /* ── ONBOARDING FUNNEL ── */
  const FunnelCard: FC = () => (
    <Card className={`${styles.spanCol3} ${styles.rowCard5}`}>
      <div className={styles.accentBarOrange} />
      <CardHead title="Onboarding Funnel" sub="All-time conversion" />
      <div className={styles.funnelBody}>
        {funnel.map((f, i) => (
          <div key={i}>
            <div className={styles.funnelRow}>
              <span className={styles.funnelStage}>{f.stage}</span>
              <div className={styles.funnelMeta}>
                <span className={styles.funnelCount}>{f.count}</span>
                <span className={styles.funnelPct}>{f.pct}%</span>
              </div>
            </div>
            <div className={styles.funnelTrack}>
              <div className={styles.funnelFill}
                style={{
                  width: `${f.pct}%`,
                  background: i === funnel.length - 1
                    ? T.blue
                    : `linear-gradient(90deg, ${T.orangeDeep}, ${T.orange})`,
                  animationDelay: `${0.6 + i * 0.05}s`,
                }} />
            </div>
          </div>
        ))}
        <div className={styles.funnelSummary}>
          <span className={styles.funnelSummaryLabel}>Conversion rate</span>
          <span className={styles.funnelSummaryValue}>69.9%</span>
        </div>
      </div>
    </Card>
  );

  /* ── DOMAIN TABLE ── */
  const DomainTable: FC = () => (
    <Card className={`${styles.spanCol5} ${styles.rowCard6}`}>
      <div className={styles.accentBarOrange} />
      <CardHead title="Domain Provisioning" sub="Latest events"
        right={<button className={styles.viewBtn}>View all →</button>} />
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr className={styles.tableHeadRow}>
              {["Domain", "Status", "SSL", "DNS", "Time"].map((h, i) => (
                <th key={i} className={styles.tableHeadCell}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {domainData.map((d, i) => (
              <tr key={i} className={styles.tableRow}>
                <td className={styles.tableCell}><span className={styles.domainName}>{d.domain}</span></td>
                <td className={styles.tableCell}><StatusPill s={d.status} /></td>
                <td className={styles.tableCell}><StatusPill s={d.ssl} /></td>
                <td className={styles.tableCell}><StatusPill s={d.dns} /></td>
                <td className={styles.tableCell}><span className={styles.timeText}>{d.time}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );

  /* ── ACTIVITY FEED ── */
  const ActivityFeed: FC = () => (
    <Card className={`${styles.spanCol3} ${styles.rowCard7}`}>
      <CardHead title="Recent Activity" sub="Live platform events"
        right={
          <div className={styles.liveBadgeSmall}>
            <span className={styles.liveDotSmall} />
            <span className={styles.liveTextSmall}>LIVE</span>
          </div>
        } />
      <div className={styles.activityBody}>
        {activity.map((a, i) => (
          <div key={i} className={styles.activityRow}>
            <div className={styles.activityIcon} style={{ background: `${a.dotColor}10` }}>
              <span className={styles.activityDot} style={{ background: a.dotColor }} />
            </div>
            <div className={styles.activityContent}>
              <div className={styles.activityText}>{a.text}</div>
              <div className={styles.activityDetail}>{a.detail}</div>
            </div>
            <div className={styles.activityTime}>{a.time}</div>
          </div>
        ))}
      </div>
    </Card>
  );

  /* ── RADIAL HEALTH ── */
  const HealthWidget: FC = () => {
    const metrics: HealthMetric[] = [
      { name: "Domains", value: 94, color: T.blue },
      { name: "APIs",    value: 80, color: T.orange },
      { name: "Subs",    value: 88, color: T.blueLight },
    ];
    return (
      <Card className={`${styles.spanCol2} ${styles.rowCard8}`}>
        <CardHead title="System Health" sub="Overall status" />
        <div className={styles.healthBody}>
          <div className={styles.healthChartWrapper}>
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart cx="50%" cy="50%" innerRadius="25%" outerRadius="85%"
                data={metrics} startAngle={90} endAngle={-270}>
                <RadialBar dataKey="value" cornerRadius={4} background={{ fill: "#ebeef8" }}>
                  {metrics.map((m, i) => <Cell key={i} fill={m.color} />)}
                </RadialBar>
                <Tooltip content={({ active, payload }: { active?: boolean; payload?: any }) =>
                  active && payload?.length ? (
                    <div className={styles.pieTooltip}>{payload[0].payload.name}: <b>{payload[0].value}%</b></div>
                  ) : null
                } />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
          <div className={styles.healthMetricList}>
            {metrics.map((m, i) => (
              <div key={i} className={styles.healthMetricRow}>
                <span className={styles.healthDot} style={{ background: m.color }} />
                <span className={styles.healthName}>{m.name}</span>
                <div className={styles.healthBar}>
                  <div className={styles.healthBarFill} style={{ width: `${m.value}%`, background: m.color }} />
                </div>
                <span className={styles.healthValue}>{m.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  };

  /* ── SPARKLINES ── */
  const SparkRow: FC = () => {
    const items: SparkItem[] = [
      { label: "New today", val: "12", spark: [3, 5, 4, 7, 6, 9, 12], color: T.blue },
      { label: "Churned",   val: "2",  spark: [4, 3, 5, 2, 4, 3, 2],  color: T.orangeDeep },
      { label: "Trials",    val: "8",  spark: [2, 4, 3, 5, 6, 7, 8],  color: T.orange },
    ];
    return (
      <div className={`${styles.sparkGrid} ${styles.sparkRow}`}>
        {items.map((it, i) => (
          <Card key={i}>
            <div className={styles.sparkBody}>
              <div className={styles.sparkLabel}>{it.label}</div>
              <div className={styles.sparkValue}>{it.val}</div>
              <svg width="100%" height="32" style={{ display: "block" }}>
                {it.spark.map((v, j) => {
                  if (j === 0) return null;
                  const max = Math.max(...it.spark);
                  const min = Math.min(...it.spark);
                  const x = (j / (it.spark.length - 1)) * 100;
                  const y = 28 - ((v - min) / (max - min || 1)) * 24;
                  const px = ((j - 1) / (it.spark.length - 1)) * 100;
                  const py = 28 - ((it.spark[j - 1] - min) / (max - min || 1)) * 24;
                  return (
                    <line key={j} x1={`${px}%`} y1={py} x2={`${x}%`} y2={y}
                      stroke={it.color} strokeWidth={2} strokeLinecap="round" />
                  );
                })}
              </svg>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <div className={styles.topBarLogo}>F</div>
          <div>
            <div className={styles.topBarTitle}>Platform Overview</div>
            <div className={styles.topBarSub}>FlyGDS Admin · Thu, Mar 5 2026</div>
          </div>
        </div>
        <div className={styles.topBarActions}>
          <div className={styles.liveBadge}>
            <span className={styles.liveDot} />
            <span className={styles.liveText}>Live</span>
          </div>
          <div className={styles.clock}>14:32 IST</div>
        </div>
      </div>

      <StatCards />

      <div className={styles.row2}>
        <RevenueChart />
        <WeeklyChart />
        <PlansChart />
      </div>
<div className={styles.row4}>
        <HealthWidget />
        <DomainTable />
        <ActivityFeed />
      </div>
      <div className={styles.row3}>
        <ApiTable />
        <FunnelCard />
      </div>

      
    </div>
  );
};

export default HomeScreen;